import {
  Button,
  Modal,
  Upload,
} from "antd";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./style.css";
import {
  ExportOutlined,
  SelectOutlined,
  ToTopOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { BASE_URL, QUOTATION_BUYER_ITEM_ACTIONS } from "../../../constants";
import { showErrorMessage, parseToListObj, showSuccessMessage, showLoader } from "../../../utils";
import dayjs from "dayjs";
import {
  getComments,
  getFiles,
  getStatus,
  postComment,
  postItems,
  performItemAction,
  postApprovalAction,
  postCommentApproval,
} from "./quotationService";
import ModalHistoryComponent from "./ModalHistoryComponent";
import RequestNudgeComponent from "../../nudge/RequestNudgeComponent";
import _, { set } from "lodash";
import { postData } from "../../../services/HomeService";
import TableComponent from "./TableComponent";
import QuoteAttachment from "./QuoteAttachment";
import QuoteNotes from "./QuoteNotes";
import { fetchData } from "../../../services/HomeService";
import QuotationRejectReasonModal from "./QuotationRejectReasonModal";
import QuotationInfoAlert from "./QuotationInfoAlert";
import BusinessDetailsModal from "../../nudge/BusinessDetailsModal";
import { getPendingApprovalItems, isRequestApprovalDisabled } from "./approvalQuotationUtils";

//TODO: fix this hackish implementation
const getUserRole = () => {
  try {
    const userData = JSON.parse(localStorage.getItem('userData') || '{}');
    const roles = userData?.roles || [];
    if (roles.includes('manager') || roles.includes('admin')) {
      return 'manager';
    }
    return 'user';
  } catch {
    return 'user';
  }
};

export default function ModalQuotationForm({
  isVisible,
  onClose,
  item,
  quotationQuoteId,
  quoteData,
  handleFileUpload,
  AvatarColor,
  isSaving,
  setIsSaving,
  data,
  actionAPI,
  setActionAPI,
  vendorData,
  setVendorData,
  setQuoteData,
  type='',
  uploadedFromList,
  setUploadedFromList,
  ComponentConfig
}) {
  const [text, setText] = useState("");
  const [files, setFiles] = useState([]);
  const [items, setItems] = useState(item?.items?.map((i) => ({ ...i })) || []);
  const [removedItems, setRemovedItems] = useState([]);
  const [submittedItems, setSubmittedItems] = useState([]);
  const [isHistoryModalVisible, setIsHistoryModalVisible] = useState(false);
  const [rejectPanelMode, setRejectPanelMode] = useState(null);
  const [approvalItems, setApprovalItems] = useState([]);
  const [batchActionLoading, setBatchActionLoading] = useState(false);
  const [popoverGrant, setPopoverGrant] = useState(false);
  const [validToken, setValidToken] = useState(null);
  const [fileList, setFileList] = useState([]);
  const [attachmentID, setAttachmentID] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [fileCounts, setFileCounts] = useState({ all: 0, sent: 0, received: 0 });
  const [itemsStatus, setItemsStatus] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [itemRef, setItemRef] = useState(null);
  const [deleteItemModal, setDeleteItemModal] = useState(false);
  const [updateClicked, setUpdateClicked] = useState(false);
  const [shouldMoveToRemovedItems, setShouldMoveToRemovedItems] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [isVendorDetailsVisible, setIsVendorDetailsVisible] = useState(false);
  const [sellerDetailsItem, setSellerDetailsItem] = useState(null);

  const [notes, setNotes] = useState([]);

  const isApprovalMode = type === 'ListApprovals';

  const displayRelation = useMemo(() => {
    if (!item?._id) return item;
    const fromList = vendorData?.find((row) => String(row._id) === String(item._id));
    return fromList ? { ...item, ...fromList } : item;
  }, [item, vendorData]);

  const listApprovalRelation = useMemo(() => {
    if (!isApprovalMode || !data?.length || !actionAPI?.entityId) return null;
    return (
      data.find((row) => String(row._id) === String(actionAPI.entityId)) ?? null
    );
  }, [isApprovalMode, data, actionAPI?.entityId]);

  /** Vendor quote-relation document id (files, status, item actions). */
  const quoteRelationId = useMemo(() => {
    if (isApprovalMode) return listApprovalRelation?._id ?? null;
    return item?._id ?? null;
  }, [isApprovalMode, listApprovalRelation?._id, item?._id]);

  /** Quotation QID — set by parent when opening the modal (URL or approval row). */
  const displayQuoteId = quotationQuoteId ?? null;

  const modalDatedAt = useMemo(() => {
    if (isApprovalMode) return listApprovalRelation?.createdAt ?? null;
    return item?.createdAt ?? null;
  }, [isApprovalMode, listApprovalRelation?.createdAt, item?.createdAt]);

  const entityId = quoteRelationId;

  const openSellerDetails = useCallback(async () => {
    const relation =
      vendorData?.find((row) => String(row._id) === String(item?._id)) || displayRelation;

    if (relation?.vendorDocs?.[0]) {
      setSellerDetailsItem(relation);
      setIsVendorDetailsVisible(true);
      return;
    }

    if (!displayQuoteId || !quoteRelationId) {
      showErrorMessage('Seller details are not available');
      return;
    }

    try {
      const quoteRes = await fetchData(`/quotations/quote/${displayQuoteId}?type=enterprise`);
      const fresh = quoteRes?.data?.find((row) => String(row._id) === String(quoteRelationId));
      if (fresh?.vendorDocs?.[0]) {
        setSellerDetailsItem(fresh);
        setIsVendorDetailsVisible(true);
      } else {
        showErrorMessage('Seller details are not available');
      }
    } catch {
      showErrorMessage('Failed to load seller details');
    }
  }, [vendorData, item?._id, displayRelation, displayQuoteId, quoteRelationId]);

  const applyFileResponse = useCallback((fileData) => {
    if (fileData && typeof fileData === 'object' && !Array.isArray(fileData) && fileData.all) {
      setFiles(fileData);
      setFileCounts({
        all: fileData.all?.length ?? 0,
        sent: fileData.sent?.length ?? 0,
        received: fileData.received?.length ?? 0,
      });
      return;
    }
    const list = Array.isArray(fileData) ? fileData : [];
    setFiles(list);
    setFileCounts({ all: list.length, sent: 0, received: 0 });
  }, []);

  const handleFiles = useCallback(() => {
    if (!quoteRelationId) return;
    const url =
      type === 'ListApprovals'
        ? `/quotations/file/${quoteRelationId}?filter=all`
        : `/quotations/file/${quoteRelationId}`;
    getFiles(url)
      .then(applyFileResponse)
      .catch((error) => console.error("Error:", error));
  }, [quoteRelationId, type, applyFileResponse]);

  useEffect(() => {
    if (type === 'ListApprovals' && listApprovalRelation?.items) {
      setApprovalItems(listApprovalRelation.items.map((row) => ({ ...row })));
    }
  }, [type, listApprovalRelation]);

  useEffect(() => {
    if (!isVisible) {
      setRejectPanelMode(null);
      setIsVendorDetailsVisible(false);
      setSellerDetailsItem(null);
    }
  }, [isVisible]);

  const refreshApprovalItems = useCallback(async () => {
    if (!quoteRelationId) return;
    const statusData = await getStatus(quoteRelationId);
    if (statusData?.items) {
      setApprovalItems(statusData.items.map((row) => ({ ...row })));
    }
  }, [quoteRelationId]);

  const refreshModalData = useCallback(async () => {
    if (!quoteRelationId) return;

    try {
      const [statusData, commentsData] = await Promise.all([
        getStatus(quoteRelationId),
        entityId ? getComments(entityId) : Promise.resolve([]),
      ]);

      if (statusData) {
        setItemsStatus(statusData);
        const freshItems = (statusData.items || []).map((row) => ({
          ...row,
          isNegotiating: false,
        }));
        setItems(freshItems);
        setRemovedItems([]);
        setSubmittedItems(freshItems.filter((i) => i.submitted));
      }

      if (commentsData) {
        setNotes(commentsData);
      }

      if (displayQuoteId && !isApprovalMode) {
        const quoteRes = await fetchData(
          `/quotations/quote/${displayQuoteId}?type=enterprise`
        );
        if (quoteRes) {
          setVendorData(quoteRes.data);
          setQuoteData(quoteRes.quoteDetails);
        }
      }

      handleFiles();
    } catch (error) {
      console.error("Error refreshing quotation modal:", error);
    }
  }, [quoteRelationId, displayQuoteId, isApprovalMode, entityId, setVendorData, setQuoteData, handleFiles]);

  useEffect(() => {
    if (isVisible && quoteRelationId) {
      refreshModalData();
      setUpdateClicked(false);
    }
  }, [isVisible, quoteRelationId, refreshModalData]);

  const addNote = () => {
    const newNote = {
      text,
      timestamp: new Date(),
    };
    postComment(quoteRelationId, newNote)
      .then((data) => {
        setNotes((notes) => [...notes, data]);
        setText("");
        refreshModalData();
      })
      .catch((error) => {
        console.error("Error:", error);
      });
  };
  const fetchQuotations = refreshModalData;

  const uploadProps = {
    name: "file",
    action: `${BASE_URL}/quotations/file/${quoteRelationId}`,
    method: "POST",
    withCredentials: true,
    headers: {},
    fileList,
    onChange: (info) => {
      let fileList = [...info.fileList];
      setFileList(fileList);
      if (info.file.status === "done") {
        const newFile = {
          actionValue: info.file.name,
        };
        setFiles([...files, newFile]);
        //clear the uplodad file from input
        setFileList([]);
        showSuccessMessage("File uploaded successfully");
        handleFiles();
      } else if (info.file.status === "error") {
        showErrorMessage("File upload failed");
      }
    }
  };
  const handleDelete = (file) => {
    setAttachmentID(file._id);
    setDeleteModal(true);
  };
  const closeDeleteModal = () => {
    setDeleteModal(false);
  };
  const handleDeleteAttachment = (id) => {
    const response =  fetch(`${BASE_URL}/activities/${id}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    }).then(response => {
      if (response.status === 200) {
        // TODO: Move this to backend call
        if (files?.all) {
          setFiles({
            all: files.all.filter((f) => f._id !== id),
            sent: files.sent.filter((f) => f._id !== id),
            received: files.received.filter((f) => f._id !== id),
          });
          setFileCounts({
            all: files.all.filter((f) => f._id !== id).length,
            sent: files.sent.filter((f) => f._id !== id).length,
            received: files.received.filter((f) => f._id !== id).length,
          });
        } else {
          const newFiles = (Array.isArray(files) ? files : []).filter((f) => f._id !== id);
          setFiles(newFiles);
          setFileCounts({ all: newFiles.length, sent: 0, received: 0 });
        }
        showSuccessMessage("File deleted successfully");
        setDeleteModal(false);
      } else {
        showErrorMessage("File deletion failed");
      }
    })
    .catch((error) => {
      console.error('Error:', error);
    });
   
  };

  const saveAllItems = (combinedItems) => {
    const newItems = (Array.isArray(combinedItems) ? combinedItems : items).map(item => {
      let status = item.itemStatus || '';
      
      if (!item.availableQuantity && !item.supplyDate && !item.quotedPrice) {
        status = '';
      } else if (item.negotiatedPrice && item.isNegotiating) {
        status = 'negotiated';
      }
      
      return {
      ...item,
      submitted: status !== '',
      itemStatus: status,
      name: item.name,
      availableQuantity: parseFloat(item.availableQuantity),
      negotiatedPrice: parseFloat(item.negotiatedPrice),
      supplyDate: item.supplyDate,
      quotedPrice: parseFloat(item.quotedPrice),
      updatedBy: 'enterprise'
    }});
    const postItemsResult = postItems(item._id, newItems);
  
    postItemsResult
      .then((data) => {
        if (!data.ok) {
          showErrorMessage("Something went wrong");
        } else {
          setSubmittedItems(newItems);
          showSuccessMessage("Saved");
          setIsSaving(true);
          setUpdateClicked(false);
          setUploadedFromList(false);
          refreshModalData();
        }
      })
      .catch((error) => {
        showErrorMessage("Something went wrong");
        console.error("Error:", error);
      });
  };

  const moveToRemovedItems = () => {
    const itemsToMove = items?.filter(item => {
      return item.itemStatus == '';
    });
    setRemovedItems(prevRemovedItems => {
  
      const updatedItemsToMove = itemsToMove.map(item => ({
        ...item,
        submitted: false,
        availableQuantity: null,
        supplyDate: null,
        quotedPrice: null,
        isRemoved: true
      }));
  
      return [...prevRemovedItems, ...updatedItemsToMove];
    });
  
    const updatedItems = items?.filter(item => {
      return item.itemStatus != ""
    }).sort((a, b) => a.itemRef - b.itemRef);
    setItems(updatedItems);
  };
  
  useEffect(() => {
    if (type !=='ListApprovals' && !uploadedFromList) {
     moveToRemovedItems();
    }
  }, []);

  useEffect(() => {
    if (shouldMoveToRemovedItems) {
      moveToRemovedItems();
      setShouldMoveToRemovedItems(false);
    }
  }, [shouldMoveToRemovedItems]);

  
  const openPopupForAuthorization = () => {
    //TODO: Fix the popup close with success failure - screen and cross origin
    const popup = window.open(`${BASE_URL}/uploads/approve-drive`, "popup", "popup=true,width=500,height=650");
    const checkPopup = setInterval(() => {
        if (popup.window.location.href.includes(`${window.location.origin}/success`)) { popup.close(); setValidToken(true); }
        if (!popup || !popup.closed) return;
        clearInterval(checkPopup);
    }, 1000 * 20);
  }
    const fetchItems = async () => {
      const data = await fetchData('/uploads/valid-token')
      setValidToken(data.success)
    };

    useEffect(() => {
      fetchItems();
    }, []);


  const handleInputChange = (index, field, type) => (event) => {
    switch (type) {
      case 'item': 
              const newItems = [...items];
              newItems[index][field] = event.target.value;
              setItems(newItems);
          break;
      case 'removeItem':
              const newRemovedItems = [...removedItems];
              newRemovedItems[index][field] = event.target.value;
              setRemovedItems(newRemovedItems);
          break;
    }
};

  const handleDateChange = (index, field, type) => (date) => {
    switch (type) {
    case 'item': 
            const newItems = [...items];
            newItems[index][field] = date;
            setItems(newItems);
        break;
    case 'removeItem':
            const newRemovedItems = [...removedItems];
            newRemovedItems[index][field] = date;
            setRemovedItems(newRemovedItems);
        break;
  }
  };

  const handleUpload = async (file) => {
    const dataForId = type === 'ListApprovals' ? {_id: data[0]?._id }: {_id: item?._id}
    setUploadingFile(true);
    const { success, errorMessage, csvData } = await handleFileUpload(file, dataForId);
    if (success) {
      const parsedCSVData = parseToListObj(csvData);
      const updatedItems = item.items?.map((item, index) => {
        const csvItem = parsedCSVData[index];
        if (csvItem ) {
          return {
            ...item,
            availableQuantity: csvItem["Available Quantity"],
            dueDate: csvItem["Due Date"],
            placeOfSupply: csvItem["Place of Supply"],
            quantity: csvItem["Quantity"],
            quotedPrice: csvItem["Quoted Price"],
            supplyDate: csvItem["Supply Date"],
            maxBudget: csvItem["Max Budget"],
            minBudget: csvItem["Min Budget"],
          };
        }
        return null;
      })
  
      setItems(updatedItems);
      setRemovedItems([]);
      setIsSaving(false);
      setUploadingFile(false);
      setUpdateClicked(true);
    } else {
      showErrorMessage(errorMessage);
    }
  };

  const handleRemove = (itemRef, type) => {
    const updatedItems = items.filter((n) => n.itemRef != itemRef);
    const updatedItemsWithStatus = updatedItems.map((item, i) => {
      return {
        ...item,
        itemStatus: item.itemStatus === 'pending' || item.itemStatus === 'approved'  ? item.itemStatus : 'accepted',
        submitted: true
      };
  });
    const removedItem = items.find((n) => n.itemRef === itemRef);
    const updatedRemovedItem = {
      ...removedItem,
      submitted: false,
      availableQuantity: null,
      supplyDate: null,
      quotedPrice: null,
      itemStatus: 'remove',
    };
  
    if(type === 'remove') {
      setRemovedItems(prevRemovedItems => [...prevRemovedItems, updatedRemovedItem]);
    } else {
      setRemovedItems([...removedItems, { ...removedItem, isRemoved: false }]);
    }

    const combinedItems = [...updatedItemsWithStatus, ...removedItems, updatedRemovedItem];
    setItems(updatedItemsWithStatus);
    if (type === 'remove') {
      postItems(item._id, combinedItems)
        .then((data) => {
          if (data.ok) {
            fetchQuotations();
            getStatus(item?._id)
            .then((data) => setItemsStatus(data))
            showSuccessMessage("Item removed successfully");
          } else {
            showErrorMessage("Failed to remove item");
          }
        })
        .catch((error) => {
          showErrorMessage("Failed to remove item");
          console.error("Error:", error);
        });
    }
  };
  
  const handleDeleteItem = (itemRef) => {
    setDeleteItemModal(false);
    handleRemove(itemRef, 'remove');
  };
  const handleDeleteForItem = (itemRef) => {
    setItemRef(itemRef);
    setDeleteItemModal(true);
  };

  const handleStartNegotiate = (index) => {
    setItems((prev) =>
      prev.map((row, i) => (i === index ? { ...row, isNegotiating: true } : row))
    );
  };
  const closeDeleteItemModal = () => {
    setDeleteItemModal(false);
  };
  const handleRestore = (index) => {
    const newItems = [...items, ...removedItems.map(item => ({ ...item, submitted: true, itemStatus: 'accepted'}))];
    saveAllItems(newItems, index); 
   
  };

  const requestApprovalDisabled = useMemo(
    () => isRequestApprovalDisabled(items),
    [items]
  );

  const handleRequestApproval = async (id) => {
    if (_.isEmpty(id) || requestApprovalDisabled) return;
    const response = await postData(`/quotations/updatestatus?id=${id}&status=approval`, {});
    if(response) {
      showSuccessMessage('Requested Successfully')
      fetchQuotations();
      onClose();
    } else {
      showErrorMessage('Something went wrong')
    }
  }

    useEffect(() => {
    setSubmittedItems(items?.filter((item) => item.submitted))
    }, [isVisible])

    const openHistoryModal = () => {
      setIsHistoryModalVisible(true)
    }

    const closeHistoryModal = () => {
      setIsHistoryModalVisible(false)
    }

    const getApprovalActionEndpoint = (actionName) =>
      actionAPI?.actions?.find((action) => action.name === actionName)?.apiEndpoint;

    const handleItemStatus = async (row, api, status) => {
      if (status !== 'approved') {
        showErrorMessage('Use reject flow for item rejection');
        return false;
      }
      if (row?.itemStatus !== 'pending') {
        showErrorMessage('Only pending items can be approved');
        return false;
      }
      const endpoint =
        api?.actions?.find((action) => action.name === 'Partial Approve')?.apiEndpoint ||
        getApprovalActionEndpoint('Partial Approve');
      if (!endpoint) {
        showErrorMessage('Partial Approve action is not available');
        return false;
      }
      const requestedData = {
        items: [{ _id: row._id, itemStatus: 'approved' }],
      };
      const result = await postApprovalAction(endpoint, requestedData);
      if (result.ok) {
        showSuccessMessage('Approved');
        await refreshApprovalItems();
        return true;
      }
      showErrorMessage(result.message || 'Something went wrong');
      return false;
    };

    const handleRequestReject = (row) => {
      if (type === 'ListApprovals') {
        if (row?.itemStatus !== 'pending') {
          showErrorMessage('Only pending items can be rejected');
          return;
        }
        setRejectPanelMode({ type: 'single', item: row });
      } else {
        setRejectPanelMode({ type: 'buyerSubmitted', item: row });
      }
    };

    const handleRejectConfirm = async (reason) => {
      if (rejectPanelMode?.type === 'buyerSubmitted') {
        const row = rejectPanelMode.item;
        if (!quoteRelationId || !row?._id) {
          showErrorMessage('Unable to reject this item');
          return;
        }

        setBatchActionLoading(true);
        try {
          const response = await performItemAction(
            quoteRelationId,
            row._id,
            QUOTATION_BUYER_ITEM_ACTIONS.REJECT,
            { reason }
          );
          if (response.ok) {
            showSuccessMessage(`Item "${row.name}" rejected`);
            setRejectPanelMode(null);
            await refreshModalData();
          } else {
            showErrorMessage(response.message || 'Failed to reject item');
          }
        } catch (error) {
          showErrorMessage('Failed to reject item');
        } finally {
          setBatchActionLoading(false);
        }
        return;
      }

      const trimmedReason = String(reason || '').trim();
      if (!trimmedReason) {
        showErrorMessage('Rejection reason is required');
        return;
      }

      // Reject All → full Reject workflow
      if (rejectPanelMode?.type === 'all') {
        const pending = getPendingApprovalItems(approvalItems);
        if (!pending.length) {
          showErrorMessage('No pending items to reject');
          return;
        }
        const endpoint = getApprovalActionEndpoint('Reject');
        if (!endpoint) {
          showErrorMessage('Reject action is not available');
          return;
        }
        setBatchActionLoading(true);
        try {
          const result = await postApprovalAction(endpoint, { reason: trimmedReason });
          if (!result.ok) {
            showErrorMessage(result.message || 'Something went wrong');
            return;
          }
          if (actionAPI?._id) {
            await postCommentApproval(actionAPI._id, {
              text: `Rejection reason: ${trimmedReason}`,
              timestamp: new Date(),
            });
          }
          showSuccessMessage(
            pending.length === 1 ? 'Item rejected' : `${pending.length} items rejected`
          );
          setRejectPanelMode(null);
          await refreshApprovalItems();
        } finally {
          setBatchActionLoading(false);
        }
        return;
      }

      // Single item → Partial Reject (rejected items only + reason)
      const row = rejectPanelMode?.item;
      if (!row?._id) {
        showErrorMessage('No pending items to reject');
        return;
      }
      if (row.itemStatus !== 'pending') {
        showErrorMessage('Only pending items can be rejected');
        return;
      }
      const endpoint = getApprovalActionEndpoint('Partial Reject');
      if (!endpoint) {
        showErrorMessage('Partial Reject action is not available');
        return;
      }
      setBatchActionLoading(true);
      try {
        const result = await postApprovalAction(endpoint, {
          reason: trimmedReason,
          items: [{ _id: row._id, itemStatus: 'rejected' }],
        });
        if (!result.ok) {
          showErrorMessage(result.message || 'Something went wrong');
          return;
        }
        if (actionAPI?._id) {
          await postCommentApproval(actionAPI._id, {
            text: `Rejection reason: ${trimmedReason}`,
            timestamp: new Date(),
          });
        }
        showSuccessMessage(`Item "${row.name}" rejected`);
        setRejectPanelMode(null);
        await refreshApprovalItems();
      } finally {
        setBatchActionLoading(false);
      }
    };

    const handleApproveAllPending = async () => {
      const pending = getPendingApprovalItems(approvalItems);
      if (!pending.length) {
        showErrorMessage('No pending items to approve');
        return;
      }
      const endpoint = getApprovalActionEndpoint('Approve');
      if (!endpoint) {
        showErrorMessage('Approve action is not available');
        return;
      }
      setBatchActionLoading(true);
      try {
        const result = await postApprovalAction(endpoint, {});
        if (!result.ok) {
          showErrorMessage(result.message || 'Something went wrong');
          return;
        }
        showSuccessMessage(
          pending.length === 1 ? 'Item approved' : `${pending.length} items approved`
        );
        await refreshApprovalItems();
      } finally {
        setBatchActionLoading(false);
      }
    };

    const renderActionButtons = () => {
      const allowedActions = item?.allowedActions || [];

      return (
        <>
          <Button
            type="primary"
            disabled={requestApprovalDisabled}
            onClick={() => handleRequestApproval(item._id)}
          >
            <ToTopOutlined /> Request Approval
          </Button>
        </>
      );
    };
    useEffect(() => {
      if (uploadedFromList) {
        setUpdateClicked(true);
      }
    }, [uploadedFromList]);
  


  return (
   
    <>
    <Modal
      open={isVisible}
      onCancel={onClose}
      destroyOnClose
      title={'Quotation'}
      footer={null}
      className="Quotes_modal Modal_qt"
    >
      <div className={`Quotes_container ${type === 'ListApprovals' ? 'Quotes_container--approval' : ''}`}>
        {type === 'ListApprovals' && (
          <QuotationInfoAlert message="Important: Buyer details will be shared with the sellers on approvals" />
        )}
        {type !== 'ListApprovals' && !requestApprovalDisabled && (
          <QuotationInfoAlert message="Important: Approval is mandatory to complete this deal" />
        )}
        <div className="Quotation_firstContainer">
          <div className="Quotation_subContainer">
            <span>
              Dated:{' '}
              <span>
                {modalDatedAt ? dayjs(modalDatedAt).format('DD/MM/YYYY') : '—'}
              </span>
            </span>
            <span>
              {type === 'ListApprovals' ? 
                <>
                  From: <span>{actionAPI?.userMeta.name}</span>
                </>
              :
                <span className="Quotation_sellerLabel">
                  Seller:{' '}
                  <span>{displayRelation?.vendorDocs?.[0]?.name || '—'}</span>
                  {displayRelation?.vendorDocs?.[0] && (
                    <button
                      type="button"
                      className="VendorDetails_extLink VendorDetails_extLink--enterprise"
                      aria-label="View seller details"
                      onClick={(event) => {
                        event.stopPropagation();
                        openSellerDetails();
                      }}
                    >
                      <ExportOutlined />
                    </button>
                  )}
                </span>
              }
              
            </span>
          </div>
          <div className="Quotation_subContainer">
            <span>
              Quote Id:{' '}
              <span>{displayQuoteId ?? '—'}</span>
            </span>
            {type === 'ListApprovals' ? (
              listApprovalRelation?.quoteFile?.[0]?.meta?.durl_link ? (
                <span className="Quotation_upload Approvals_ilink" style={{ cursor: 'pointer', color: '#4800FF' }}>
                  <a
                    href={listApprovalRelation.quoteFile[0].meta.durl_link}
                    target="_blank"
                    rel="noreferrer"
                    style={{ color: 'inherit' }}
                  >
                    Submitted quote <SelectOutlined style={{ cursor: 'pointer', color: '#4800FF' }} />
                  </a>
                </span>
              ) : (
                <span className="Approvals_ilink">Submitted quote <SelectOutlined /></span>
              )
            ) : 
              (item.quoteFile && item.quoteFile.length >0 ? (
                <span className="Quotation_upload Approvals_ilink" style={{ cursor: 'pointer', color: '#4800FF' }}>
                  <a href={item.quoteFile[0]?.meta?.durl_link} target="_blank" rel="noreferrer" style={{color: 'inherit'}} >
                    Submitted Quote <SelectOutlined style={{ cursor: 'pointer', color: '#4800FF' }} />
                  </a>
                </span>
              ) 
              : null 
            )}
          </div>
        </div>
        {quoteData && isApprovalMode && (
          <TableComponent
            quoteData={quoteData}
            openHistoryModal={openHistoryModal}
            itemsStatus={itemsStatus}
            type="viewData"
            modalType={type}
            items={approvalItems}
            actionAPI={actionAPI}
            handleItemStatus={handleItemStatus}
            onRequestReject={handleRequestReject}
            quoteRelationId={quoteRelationId}
          />
        )}

        {quoteData && !isApprovalMode && (
          <TableComponent
            items={items}
            quoteData={quoteData}
            openHistoryModal={openHistoryModal}
            handleRemove={handleRemove}
            handleDeleteForItem={handleDeleteForItem}
            submittedItems={submittedItems}
            handleInputChange={handleInputChange}
            handleDateChange={handleDateChange}
            setIsSaving={setIsSaving}
            saveAllItems={saveAllItems}
            itemsStatus={itemsStatus}
            isSaving={isSaving}
            updateClicked={updateClicked}
            setUpdateClicked={setUpdateClicked}
            onStartNegotiate={handleStartNegotiate}
            removedItems={removedItems}
            handleRestore={handleRestore}
            type="editData"
            modalType={type}
            showUpdateButton={false}
            quoteRelationId={quoteRelationId}
            onItemActionComplete={refreshModalData}
            onRequestReject={handleRequestReject}
            userRole={getUserRole()}
          />
        )}
        <QuoteAttachment 
          files={files}
          handleDelete={handleDelete}
          popoverGrant={popoverGrant} 
          validToken={validToken} 
          openPopupForAuthorization={openPopupForAuthorization}
          setPopoverGrant={setPopoverGrant}
          handleFiles={handleFiles}
          fileCounts={fileCounts}
          type2={type === 'ListApprovals' ? 'ModalApprovals' : undefined}
          />

          <QuoteNotes
            notes={notes}
            text={text}
            setText={setText}
            uploadProps={uploadProps}
            addNote={addNote}
            type2={type}
            />
      </div>
      <div className="Quotes_footer">
      {type === 'ListApprovals' ? (
        <div className="Approval_footer">
          <Button
            type="primary"
            loading={batchActionLoading}
            disabled={!getPendingApprovalItems(approvalItems).length}
            onClick={handleApproveAllPending}
          >
            Approve All
          </Button>
          <Button
            danger
            disabled={!getPendingApprovalItems(approvalItems).length}
            onClick={() => setRejectPanelMode({ type: 'all' })}
          >
            Reject All
          </Button>
        </div>
      ) : (
        renderActionButtons()
      )}
      </div>
      {isLoading && showLoader('Requesting')}
      {uploadingFile && showLoader('Processing CSV...')}
    </Modal>

    <ModalHistoryComponent
      isVisible={isHistoryModalVisible}
      onClose={closeHistoryModal}
      AvatarColor={AvatarColor}
      name={quoteData?.title}
      id={quoteRelationId}
      />
    {deleteModal && <RequestNudgeComponent
        type={'deleteAttachment'}
        data={attachmentID}
        isVisible={deleteModal}
        onClose={closeDeleteModal}
        handleNudgeSubmit={handleDeleteAttachment}
      />}
    {deleteItemModal &&
    <RequestNudgeComponent
        type={'removeItem'}
        data={itemRef}
        isVisible={deleteItemModal}
        onClose={closeDeleteItemModal}
        handleNudgeSubmit={handleDeleteItem}
      />
    }
    <QuotationRejectReasonModal
      open={!!rejectPanelMode}
      mode={rejectPanelMode?.type}
      items={
        rejectPanelMode?.type === 'all'
          ? getPendingApprovalItems(approvalItems)
          : rejectPanelMode?.item
            ? [
                approvalItems.find((row) => row._id === rejectPanelMode.item._id) ||
                  rejectPanelMode.item,
              ]
            : []
      }
      loading={batchActionLoading}
      onCancel={() => setRejectPanelMode(null)}
      onConfirm={handleRejectConfirm}
    />
    {isVendorDetailsVisible && sellerDetailsItem && (
      <BusinessDetailsModal
        isVisible={isVendorDetailsVisible}
        onClose={() => {
          setIsVendorDetailsVisible(false);
          setSellerDetailsItem(null);
        }}
        item={sellerDetailsItem}
        avatarStyle={AvatarColor?.[0]?.style}
      />
    )}
    </>
  );
}
